// GL01 합배송출고 — React Query 훅.
import { useMutation } from '@tanstack/react-query'
import type { UseMutationResult } from '@tanstack/react-query'
import { useFetchAllRows, type UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { confirmGlobalOutbounds, saveConsolOutbounds, searchConsolOutbounds, syncConsolOutbounds } from './apis'
import { toRow } from './mapping'
import { GL01_FETCH_CHUNK_SIZE } from './types'
import type {
  ConfirmOutboundReq,
  ConfirmOutboundRes,
  ConsolOutboundFilter,
  ConsolOutboundRow,
  ConsolOutboundSaveReq,
  ConsolOutboundSaveRes,
} from './types'

/**
 * 메뉴코드는 소문자 'gl15' 다. 탭을 닫을 때 `MenuOnSessionWrap.remove` 가 queryKey 요소를 훑어
 * 메뉴코드가 든 캐시만 지우는데(:110-115), 그 코드가 `MENU_CD.toLowerCase()` 로 만들어진다
 * (useAuthQueries.ts:54). 대문자로 넣으면 `.includes` 가 안 걸려 캐시가 남는다.
 */
export const consolOutboundQueryKeys = {
  all: ['gl15', 'GLOBAL_CONSOL_OUTBOUND'] as const,
  list: (params: ConsolOutboundFilter | null) => [...consolOutboundQueryKeys.all, 'list', params] as const,
}

const getKey = (r: ConsolOutboundRow) => r._id

/**
 * 조회 — 전량을 CHUNK_SIZE 구간으로 끝까지 받는다(LOGISPRD-15271). 필수 필터(화주)가 없으면 호출하지 않는다.
 * 같은 조건의 앱 메뉴 탭 복귀는 캐시를 쓰고, 같은 조건을 다시 읽는 [조회] 재클릭·저장·확정 뒤 갱신은 소비 측이 refetch 한다.
 */
export const useGetConsolOutbounds = (params: ConsolOutboundFilter | null): UseFetchAllRowsResult<ConsolOutboundRow> =>
  useFetchAllRows({
    queryKey: consolOutboundQueryKeys.list(params),
    fetchPage: async (page) => {
      if (!params) return { items: [], totalCount: 0 } // enabled 가 막지만 단언 대신 여기서 거른다(#1017 리뷰)
      const res = await searchConsolOutbounds({ ...params, page, size: GL01_FETCH_CHUNK_SIZE })
      const items = (res.data?.items ?? []).map(toRow)
      return { items, totalCount: res.data?.pageInfo?.totalCount ?? items.length }
    },
    chunkSize: GL01_FETCH_CHUNK_SIZE,
    getKey,
    enabled: !!params?.shipperCode,
  })

/** 저장 — 편집한 행의 메모 4필드를 updates 로 보낸다. */
export const useSaveConsolOutbounds = (): UseMutationResult<ApiResponseV2<ConsolOutboundSaveRes>, Error, ConsolOutboundSaveReq> =>
  useMutation({ mutationFn: saveConsolOutbounds })

/** 동기화 — 즉시 동기화 요청(본문 없음). */
export const useSyncConsolOutbounds = (): UseMutationResult<ApiResponseV2<unknown>, Error, void> =>
  useMutation({ mutationFn: syncConsolOutbounds })

/** 출고확정 · 잔여출고확정 — doType 으로 나뉜다. */
export const useConfirmGlobalOutbounds = (): UseMutationResult<ConfirmOutboundRes, Error, ConfirmOutboundReq> =>
  useMutation({ mutationFn: confirmGlobalOutbounds })
